import React, { useRef } from 'react';
import {
  FiFileText, FiUser, FiBriefcase, FiHash, FiMail, FiPhone, FiAward, FiMapPin,
  FiClock, FiCalendar, FiSettings, FiEye, FiCompass, FiLayers, FiUsers, FiUserCheck,
  FiUserX, FiCpu, FiZap, FiX, FiCheckCircle, FiAlertCircle, FiTag, FiHome,
} from 'react-icons/fi';
import { DOC_CSS_SCOPED } from '../../utils/docCss';
import { sanitizeDocHtml } from '../../utils/sanitizeHtml';
import { useDocumentExport } from '../../hooks/useDocumentExport';
import { buildDocBasename } from '../../utils/downloadFilename';
import ExportMenu from '../common/ExportMenu';
import { AGENDA_PRESETS } from '../../context/MeetingConfigContext';
import EntityAuditHistory from './EntityAuditHistory';
import HoverPopover from '../common/HoverPopover';

function SectionCard({ icon, title, children, accent = '#145f5f' }) {
  return (
    <div
      className="bg-white rounded-[12px] border overflow-hidden"
      style={{ borderColor: '#e8e2d6', boxShadow: '0 1px 3px rgba(8,17,31,.04)' }}
    >
      <div
        className="px-4 py-2.5 border-b flex items-center gap-2"
        style={{ borderColor: '#e8e2d6', background: 'linear-gradient(180deg, #fdfbf6 0%, #ffffff 100%)' }}
      >
        <span className="w-[3px] h-3.5 rounded-full shrink-0" style={{ background: accent }} />
        {icon && <span className="text-[12px]" style={{ color: accent }}>{icon}</span>}
        <span className="text-[11.5px] font-bold text-[#08111f] tracking-tight">{title}</span>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  );
}

function InfoRow({ icon, label, value, mono = false }) {
  if (!value && value !== 0) return null;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-[#7a7060] mb-0.5">
        {icon && <span className="text-[10px] text-[#145f5f]">{icon}</span>}
        <span>{label}</span>
      </div>
      <div
        className={`text-[12px] text-[#08111f] break-words ${mono ? '' : 'font-medium'}`}
        style={mono ? { fontFamily: "'JetBrains Mono', monospace" } : undefined}
      >
        {value}
      </div>
    </div>
  );
}

// Keep in sync with VISIBILITY_BADGE in MyDocumentsPage. Promote to a
// shared util once "restricted" lands.
const VISIBILITY_INFO = {
  public: {
    label: 'Public',
    color: '#4a6b3f',
    description:
      "Visible to the corporate owner and every professional assigned to this company (subject to each assignment's access-level rules).",
  },
  private: {
    label: 'Private',
    color: '#9a564a',
    description:
      "Visible only to the corporate owner and the specific user this draft was assigned to. Other professionals on the same company can't see it.",
  },
  personal: {
    label: 'Personal',
    color: '#145f5f',
    description:
      "No company attached. Visible only to the creator, and locked — personal drafts can't be changed to Public or Private.",
  },
};

function OrderDetailModal({ order: draft, onClose }) {
  const contentRef = useRef(null);

  const exportActions = useDocumentExport(contentRef, {
    title: draft?.documentTitle || 'Document',
    filename: buildDocBasename({ company: draft?.companyName, docType: draft?.documentTitle || 'Document' }),
    fallbackHtml: draft?.docHtml || '',
  });

  const sourceChipStyle = draft.docSource === 'ai'
    ? { background: '#e3f0ed', color: '#145f5f', icon: <FiZap className="text-[9px]" /> }
    : draft.docSource === 'fallback'
    ? { background: '#f3ebd7', color: '#8a6a1f', icon: <FiAlertCircle className="text-[9px]" /> }
    : draft.docSource === 'user-edited'
    ? { background: '#edeef3', color: '#3a4a66', icon: <FiCheckCircle className="text-[9px]" /> }
    : { background: '#f2eee6', color: '#8a7d66', icon: <FiEye className="text-[9px]" /> };

  const sourceLabel = draft.docSource === 'ai' ? 'AI-generated'
    : draft.docSource === 'fallback' ? 'Fallback'
    : draft.docSource === 'user-edited' ? 'User Edited'
    : 'Preview';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{ background: 'rgba(8,17,31,0.6)' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[16px] flex flex-col mx-3 overflow-hidden"
        style={{
          width: 'min(1120px, 96vw)',
          maxHeight: '95vh',
          boxShadow: '0 30px 60px -15px rgba(8,17,31,.4), 0 0 0 1px rgba(221,214,198,.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >

        {/* Header — sticky */}
        <div
          className="relative flex items-center justify-between px-6 py-4 border-b shrink-0"
          style={{ borderColor: '#e8e2d6', background: 'linear-gradient(135deg, #f8f5ef 0%, #fdfbf6 60%, #ffffff 100%)' }}
        >
          <div className="flex items-start gap-3 flex-1 min-w-0 mr-4">
            {/* Document type icon */}
            <div
              className="w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0 shadow-sm"
              style={{ background: 'linear-gradient(135deg, #1a7373 0%, #145f5f 100%)' }}
            >
              <FiFileText className="text-white text-[18px]" />
            </div>

            <div className="flex-1 min-w-0">
              <h3
                className="text-[20px] font-bold text-[#08111f] leading-tight truncate"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {draft.documentTitle}
              </h3>
              <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
                <span
                  className="text-[10px] text-[#7a7060] bg-[#f0ebe0] px-1.5 py-[1px] rounded"
                  style={{ fontFamily: "'JetBrains Mono', monospace" }}
                >
                  {draft.documentId}
                </span>
                <span
                  className="inline-flex items-center gap-1 text-[9.5px] px-2 py-[2px] rounded-full font-bold"
                  style={{ background: sourceChipStyle.background, color: sourceChipStyle.color }}
                >
                  {sourceChipStyle.icon}
                  {sourceLabel}
                </span>
                <span
                  className={`inline-flex items-center gap-1 text-[9.5px] px-2 py-[2px] rounded-full font-bold ${draft.isDeleted ? 'bg-[#f2e9e6] text-[#9a564a]' : 'bg-[#e8efe5] text-[#4a6b3f]'}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${draft.isDeleted ? 'bg-[#9a564a]' : 'bg-[#4a6b3f]'}`} />
                  {draft.isDeleted ? 'Deleted' : 'Active'}
                </span>
              </div>
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex items-center gap-1.5 shrink-0">
            <ExportMenu
              size="sm"
              items={[
                { key: 'copy',  icon: '📋', label: 'Copy text',     onClick: exportActions.copy },
                { key: 'txt',   icon: '⬇',  label: 'Download .TXT',  onClick: exportActions.downloadTxt, dividerBefore: true },
                { key: 'html',  icon: '⬇',  label: 'Download .HTML', onClick: exportActions.downloadHtml },
                { key: 'print', icon: '🖨', label: 'Print / PDF',    onClick: exportActions.print },
              ]}
            />
            <button
              onClick={onClose}
              aria-label="Close"
              className="ml-1 w-8 h-8 rounded-full flex items-center justify-center text-[#7a7060] hover:bg-white hover:text-[#08111f] hover:shadow-sm transition-all"
            >
              <FiX className="text-[16px]" />
            </button>
          </div>
        </div>

        {/* Body — scrollable */}
        <div
          className="flex-1 overflow-y-auto cs-content-scroll"
          style={{ background: 'linear-gradient(180deg, #f4f0e8 0%, #ece6d8 100%)' }}
        >
          <div className="flex gap-6 p-6">

            {/* Left: Info panel */}
            <div className="w-[290px] shrink-0 space-y-4">
              {/* Document Info */}
              <SectionCard icon={<FiFileText />} title="Document Info">
                <InfoRow icon={<FiTag />} label="Type" value={draft.documentType} />
                <InfoRow icon={<FiCompass />} label="Origin" value={draft.pageOrigin} />
                <InfoRow icon={<FiLayers />} label="Source" value={draft.docSource} />
                <InfoRow
                  icon={<FiEye />}
                  label="Visibility"
                  value={(() => {
                    const v = draft.visibility || 'private';
                    const info = VISIBILITY_INFO[v];
                    if (!info) {
                      return <span style={{ textTransform: 'capitalize' }}>{v}</span>;
                    }
                    return (
                      <HoverPopover
                        width={280}
                        content={
                          <>
                            <div className="mb-1.5 font-semibold" style={{ color: info.color }}>
                              {info.label}
                            </div>
                            <div>{info.description}</div>
                          </>
                        }
                      >
                        <span style={{ textTransform: 'capitalize', cursor: 'help' }}>
                          {v}
                        </span>
                      </HoverPopover>
                    );
                  })()}
                />
                <InfoRow icon={<FiBriefcase />} label="Company" value={draft.companyName} />
                <InfoRow icon={<FiHash />} label="CIN" value={draft.companyCin} mono />
              </SectionCard>

              {/* Created By */}
              {draft.user && (
                <SectionCard icon={<FiUser />} title="Created By">
                  <InfoRow icon={<FiUser />} label="Name" value={draft.user.fullName} />
                  <InfoRow icon={<FiMail />} label="Email" value={draft.user.email} />
                  <InfoRow icon={<FiPhone />} label="Mobile" value={draft.user.mobile} mono />
                  <InfoRow icon={<FiBriefcase />} label="Company" value={draft.user.companyName} />
                  <InfoRow icon={<FiUser />} label="Designation" value={draft.user.designation} />
                  <InfoRow icon={<FiAward />} label="Profession" value={draft.user.profession} />
                  <InfoRow icon={<FiHash />} label="Membership #" value={draft.user.membershipNo} mono />
                  <InfoRow icon={<FiHash />} label="CIN" value={draft.user.cin} mono />
                  <InfoRow icon={<FiMapPin />} label="State" value={draft.user.state} />
                </SectionCard>
              )}

              {/* AI Metadata */}
              {draft.draftMeta && (draft.draftMeta.model || draft.draftMeta.latencyMs != null) && (
                <SectionCard icon={<FiCpu />} title="AI Details" accent="#836016">
                  <InfoRow icon={<FiCpu />} label="Model" value={draft.draftMeta.model} />
                  <InfoRow icon={<FiZap />} label="Latency" value={draft.draftMeta.latencyMs != null ? `${draft.draftMeta.latencyMs}ms` : null} mono />
                  <InfoRow icon={<FiClock />} label="Generated" value={draft.draftMeta.generatedAt ? new Date(draft.draftMeta.generatedAt).toLocaleString('en-IN') : null} />
                  <InfoRow icon={<FiCheckCircle />} label="Cache Hit" value={draft.draftMeta.cacheHit != null ? (draft.draftMeta.cacheHit ? 'Yes' : 'No') : null} />
                </SectionCard>
              )}

              {/* Minutes Config (for meeting minutes) */}
              {draft.documentType === 'minutes' && draft.minutesConfig && (() => {
                const mc = draft.minutesConfig;
                const cfg = mc.config || {};
                const typeLabels = { board: 'Board Meeting', agm: 'Annual General Meeting (AGM)', egm: 'Extraordinary General Meeting (EGM)', committee: 'Committee Meeting' };
                const presets = AGENDA_PRESETS[cfg.meetingType] || AGENDA_PRESETS.board || [];
                const activeAgenda = presets.filter((p) => (mc.agendaChecked || {})[p.id]).map((p) => p.label);
                const customAgenda = mc.customItems || [];
                const presentDirs = Object.entries(mc.directorsChecked || {}).filter(([, v]) => v).map(([k]) => k);
                const customDirs = mc.customDirectors || [];
                const absentDirs = mc.absentDirectors || [];
                const inviteesList = mc.invitees || [];

                return (
                  <SectionCard icon={<FiSettings />} title="Meeting Settings">
                    <InfoRow icon={<FiUsers />} label="Meeting Type" value={typeLabels[cfg.meetingType] || cfg.meetingType} />
                    <InfoRow icon={<FiHash />} label="Meeting Number" value={cfg.meetingNo === 'custom' ? cfg.meetingNoCustom : cfg.meetingNo} />
                    <InfoRow icon={<FiCalendar />} label="Financial Year" value={cfg.fy} />
                    <InfoRow icon={<FiCalendar />} label="Date" value={cfg.date} />
                    <InfoRow icon={<FiClock />} label="Time" value={cfg.startTime && cfg.endTime ? `${cfg.startTime} \u2014 ${cfg.endTime}` : cfg.startTime} />
                    <InfoRow icon={<FiHome />} label="Mode" value={cfg.mode} />
                    <InfoRow icon={<FiMapPin />} label="Venue" value={cfg.venue} />
                    <InfoRow icon={<FiUserCheck />} label="Chairperson" value={cfg.chairperson} />
                    <InfoRow icon={<FiUser />} label="Company Secretary" value={cfg.companySecretary} />
                    <InfoRow icon={<FiAward />} label="Statutory Auditor" value={cfg.statutoryAuditor} />
                    <InfoRow icon={<FiCalendar />} label="Notice Date" value={cfg.noticeDate} />
                    <InfoRow icon={<FiClock />} label="Previous Meeting" value={cfg.previousMeetingDate ? `${cfg.previousMeetingNumber || ''} (${cfg.previousMeetingDate})`.trim() : null} />

                    {(activeAgenda.length > 0 || customAgenda.length > 0) && (
                      <div className="pt-2 border-t" style={{ borderColor: '#f0ebe0' }}>
                        <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-[#7a7060] mb-1.5">
                          <FiLayers className="text-[10px] text-[#145f5f]" />
                          Agenda Items
                        </div>
                        <ul className="text-[11px] text-[#08111f] space-y-0.5 list-disc list-inside">
                          {activeAgenda.map((label, i) => <li key={i}>{label}</li>)}
                          {customAgenda.map((label, i) => <li key={`c${i}`} className="text-[#145f5f]">{label} <span className="text-[#7a7060] font-normal italic">(custom)</span></li>)}
                        </ul>
                      </div>
                    )}

                    {(presentDirs.length > 0 || customDirs.length > 0) && (
                      <div className="pt-2 border-t" style={{ borderColor: '#f0ebe0' }}>
                        <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-[#7a7060] mb-1.5">
                          <FiUserCheck className="text-[10px] text-[#4a6b3f]" />
                          Directors Present
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {presentDirs.map((d) => (
                            <span key={d} className="px-2 py-0.5 rounded-full text-[9px] font-semibold" style={{ background: '#145f5f22', color: '#145f5f' }}>{d}</span>
                          ))}
                          {customDirs.map((d, i) => {
                            const dir = typeof d === 'string' ? { name: d } : (d || {});
                            return (
                              <span key={`cd${i}`} className="px-2 py-0.5 rounded-full text-[9px] font-semibold bg-[#edeef3] text-[#3a4a66]">
                                {dir.name}{dir.din ? ` (${dir.din})` : ''}{dir.designation ? ` \u2014 ${dir.designation}` : ''}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {absentDirs.length > 0 && (
                      <div className="pt-2 border-t" style={{ borderColor: '#f0ebe0' }}>
                        <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-[#7a7060] mb-1.5">
                          <FiUserX className="text-[10px] text-[#8a6a1f]" />
                          Absent Directors
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {absentDirs.map((d, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full text-[9px] font-semibold bg-[#f3ebd7] text-[#8a6a1f]">{typeof d === 'string' ? d : d.name || JSON.stringify(d)}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {inviteesList.length > 0 && (
                      <div className="pt-2 border-t" style={{ borderColor: '#f0ebe0' }}>
                        <div className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wider text-[#7a7060] mb-1.5">
                          <FiUsers className="text-[10px] text-[#6f6049]" />
                          Invitees
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {inviteesList.map((inv, i) => (
                            <span key={i} className="px-2 py-0.5 rounded-full text-[9px] font-semibold bg-[#efe9e0] text-[#6f6049]">{typeof inv === 'string' ? inv : inv.name || JSON.stringify(inv)}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </SectionCard>
                );
              })()}

              {/* Timestamps */}
              <SectionCard icon={<FiClock />} title="Timestamps" accent="#7a7060">
                <InfoRow icon={<FiCalendar />} label="Created" value={new Date(draft.createdAt).toLocaleString('en-IN')} />
                <InfoRow icon={<FiClock />} label="Updated" value={new Date(draft.updatedAt).toLocaleString('en-IN')} />
              </SectionCard>

              {/* Audit history for this draft */}
              <EntityAuditHistory entityType="DraftDocument" entityId={draft._id} limit={20} />
            </div>

            {/* Right: Document Preview */}
            <div className="flex-1 min-w-0 flex justify-center">
              <div
                className="bg-white rounded-[12px] overflow-hidden w-full"
                style={{
                  maxWidth: '820px',
                  boxShadow: '0 12px 48px rgba(8,17,31,.18), 0 2px 6px rgba(8,17,31,.06)',
                }}
              >
                <style>{DOC_CSS_SCOPED}</style>
                <div
                  ref={contentRef}
                  dangerouslySetInnerHTML={{ __html: sanitizeDocHtml(draft.docHtml || '<p style="color:#999;text-align:center;padding:40px">No document content available</p>') }}
                  style={{
                    padding: '48px 56px',
                    fontFamily: "'Times New Roman', Times, serif",
                    backgroundColor: '#fff',
                    fontSize: '13pt',
                    lineHeight: 1.8,
                    color: '#000',
                    minHeight: '760px',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderDetailModal;
